import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Users, Search, Filter, Star, Eye, Check, X, Mail, Video, Calendar, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
// Get the base URL for frontend links
const getBaseUrl = () => {
  // Remove /api if present, or just use the URL as-is for frontend
  const url = BACKEND_URL || window.location.origin;
  return url.replace(/\/api\/?$/, '');
};

const statusColors = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
  reviewed: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
  accepted: 'bg-green-500/20 text-green-400 border-green-500/50',
  rejected: 'bg-red-500/20 text-red-400 border-red-500/50',
};

const typeColors = {
  volunteer: 'bg-purple-500/20 text-purple-400',
  paid: 'bg-green-500/20 text-green-400',
};

// Normalize a user-pasted URL (add protocol if missing) and derive an
// embeddable player URL for YouTube / Vimeo / Google Drive when possible.
const parseVideoUrl = (rawUrl) => {
  if (!rawUrl) return { external: '', embed: null };

  let url = rawUrl.trim();
  // Add https:// if the user pasted a bare link like "youtube.com/..."
  if (!/^https?:\/\//i.test(url)) {
    url = `https://${url.replace(/^\/+/, '')}`;
  }

  let embed = null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '').toLowerCase();

    // YouTube (watch, youtu.be, shorts, embed)
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      let id = u.searchParams.get('v');
      if (!id && u.pathname.startsWith('/shorts/')) id = u.pathname.split('/')[2];
      if (!id && u.pathname.startsWith('/embed/')) id = u.pathname.split('/')[2];
      if (id) embed = `https://www.youtube.com/embed/${id}`;
    } else if (host === 'youtu.be') {
      const id = u.pathname.split('/')[1];
      if (id) embed = `https://www.youtube.com/embed/${id}`;
    }
    // Vimeo
    else if (host === 'vimeo.com') {
      const id = u.pathname.split('/').filter(Boolean)[0];
      if (id && /^\d+$/.test(id)) embed = `https://player.vimeo.com/video/${id}`;
    } else if (host === 'player.vimeo.com') {
      embed = url;
    }
    // Google Drive shared file -> preview embed
    else if (host === 'drive.google.com') {
      const match = u.pathname.match(/\/file\/d\/([^/]+)/);
      const id = match ? match[1] : u.searchParams.get('id');
      if (id) embed = `https://drive.google.com/file/d/${id}/preview`;
    }
  } catch (e) {
    // Leave embed null; external link still works
  }

  return { external: url, embed };
};

// Scoring system - calculates a score from 0-100 based on application quality
const calculateScore = (application) => {
  let score = 0;
  
  // Experience (20 points)
  if (application.worked_conventions_before === 'yes') {
    score += 15;
    if (application.previous_experience && application.previous_experience.length > 50) {
      score += 5;
    }
  }
  
  // Answer quality - based on response length and completeness (40 points)
  const responses = [
    application.favorite_anime_reason,
    application.why_great_fit,
    application.poc_representation_importance
  ];
  
  responses.forEach((response, index) => {
    if (response) {
      const length = response.length;
      if (length > 200) score += 13;
      else if (length > 100) score += 10;
      else if (length > 50) score += 7;
      else score += 3;
    }
  });
  
  // Video provided (20 points)
  if (application.video_url && application.video_url.length > 5) {
    score += 20;
  }
  
  // Social media presence (10 points)
  if (application.social_media_handle && application.social_media_handle.length > 2) {
    score += 10;
  }
  
  // Additional details provided (10 points)
  if (application.additional_details && application.additional_details.length > 30) {
    score += 10;
  }
  
  return Math.min(score, 100);
};

const ScoreBadge = ({ score }) => {
  let colorClass = 'bg-red-500/20 text-red-400';
  let label = 'Low';
  
  if (score >= 80) {
    colorClass = 'bg-green-500/20 text-green-400';
    label = 'Excellent';
  } else if (score >= 60) {
    colorClass = 'bg-blue-500/20 text-blue-400';
    label = 'Good';
  } else if (score >= 40) {
    colorClass = 'bg-yellow-500/20 text-yellow-400';
    label = 'Average';
  }
  
  return (
    <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${colorClass}`}>
      <Star className="h-3 w-3" />
      {score} - {label}
    </div>
  );
};

export default function StaffApplicants({ embedded = false }) {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterEvent, setFilterEvent] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterApplications();
  }, [applications, searchTerm, filterStatus, filterType, filterEvent]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [appsRes, eventsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/staff-applications`, { headers }),
        axios.get(`${BACKEND_URL}/api/events`, { headers }),
      ]);

      // Add scores to applications
      const appsWithScores = appsRes.data.map(app => ({
        ...app,
        score: calculateScore(app)
      }));

      // Sort by score (highest first)
      appsWithScores.sort((a, b) => b.score - a.score);

      setApplications(appsWithScores);
      setEvents(eventsRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied. Management only.');
        navigate('/');
      } else {
        toast.error('Failed to load applications');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filterApplications = () => {
    let filtered = applications;

    if (searchTerm) {
      filtered = filtered.filter(
        (app) =>
          app.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          app.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          app.event_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter((app) => app.status === filterStatus);
    }

    if (filterType !== 'all') {
      filtered = filtered.filter((app) => app.application_type === filterType);
    }

    if (filterEvent !== 'all') {
      filtered = filtered.filter((app) => app.event_id === filterEvent);
    }

    setFilteredApplications(filtered);
  };

  const handleStatusUpdate = async (appId, newStatus) => {
    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.put(
        `${BACKEND_URL}/api/staff-applications/${appId}/status?status=${newStatus}${notes ? `&notes=${encodeURIComponent(notes)}` : ''}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Application ${newStatus}`);
      setDetailDialogOpen(false);
      setNotes('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
      console.error(error);
    }
  };

  const handleDelete = async (appId) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    
    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.delete(`${BACKEND_URL}/api/staff-applications/${appId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Application deleted');
      setDetailDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete application');
      console.error(error);
    }
  };

  const openDetail = (app) => {
    setSelectedApp(app);
    setNotes(app.notes || '');
    setDetailDialogOpen(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const copyApplicationLink = async (eventId, type) => {
    const baseUrl = getBaseUrl();
    const link = `${baseUrl}/apply/${type}/${eventId}`;
    console.log('Copying link:', link); // Debug log
    try {
      await navigator.clipboard.writeText(link);
      toast.success(`${type === 'volunteer' ? 'Volunteer' : 'Paid'} application link copied!`);
    } catch (err) {
      console.error('Clipboard error:', err);
      // Fallback for mobile/older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = link;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const success = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (success) {
          toast.success(`${type === 'volunteer' ? 'Volunteer' : 'Paid'} application link copied!`);
        } else {
          toast.error('Failed to copy. Link: ' + link);
        }
      } catch (fallbackErr) {
        console.error('Fallback copy error:', fallbackErr);
        // Show the link in a prompt so user can manually copy
        window.prompt('Copy this link:', link);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading applications...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {!embedded && (
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold font-heading tracking-tight" data-testid="staff-applicants-title">
            Staff Applicants
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {applications.length} total applications
          </p>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-2 sm:gap-4">
        <Card className="bg-yellow-500/10 border-yellow-500/30">
          <CardContent className="p-2 sm:p-4">
            <p className="text-[10px] sm:text-sm text-muted-foreground">Pending</p>
            <p className="text-lg sm:text-2xl font-bold text-yellow-400">
              {applications.filter(a => a.status === 'pending').length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-blue-500/10 border-blue-500/30">
          <CardContent className="p-2 sm:p-4">
            <p className="text-[10px] sm:text-sm text-muted-foreground">Reviewed</p>
            <p className="text-lg sm:text-2xl font-bold text-blue-400">
              {applications.filter(a => a.status === 'reviewed').length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-green-500/10 border-green-500/30">
          <CardContent className="p-2 sm:p-4">
            <p className="text-[10px] sm:text-sm text-muted-foreground">Accepted</p>
            <p className="text-lg sm:text-2xl font-bold text-green-400">
              {applications.filter(a => a.status === 'accepted').length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="p-2 sm:p-4">
            <p className="text-[10px] sm:text-sm text-muted-foreground">Rejected</p>
            <p className="text-lg sm:text-2xl font-bold text-red-400">
              {applications.filter(a => a.status === 'rejected').length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Generate Links Section */}
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="text-base sm:text-lg">Generate Application Links</CardTitle>
          <p className="text-xs text-muted-foreground">Select an event to copy its application link</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground">No events available. Create an event first.</p>
          ) : (
            events.map(event => (
              <div key={event.id} className="p-3 bg-secondary/30 rounded-lg">
                <p className="font-medium text-sm mb-2 truncate">{event.name}</p>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="flex-1 text-xs"
                    onClick={() => copyApplicationLink(event.id, 'volunteer')}
                  >
                    Copy Volunteer Link
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="flex-1 text-xs"
                    onClick={() => copyApplicationLink(event.id, 'paid')}
                  >
                    Copy Paid Link
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-col gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or event..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-applications-input"
            className="pl-10 bg-secondary/50 border-transparent focus:border-primary"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          data-testid="filter-status-select"
          className="px-4 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="all">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="reviewed">Reviewed</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
        </select>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          data-testid="filter-type-select"
          className="px-4 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="all">All Types</option>
          <option value="volunteer">Volunteer</option>
          <option value="paid">Paid</option>
        </select>
        <select
          value={filterEvent}
          onChange={(e) => setFilterEvent(e.target.value)}
          data-testid="filter-event-select"
          className="px-4 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="all">All Events</option>
          {events.map(event => (
            <option key={event.id} value={event.id}>{event.name}</option>
          ))}
        </select>
      </div>

      {/* Applications List */}
      {filteredApplications.length === 0 ? (
        <Card className="p-8 sm:p-12 text-center bg-card border-border/50">
          <Users className="h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground mx-auto mb-4" />
          <div className="text-muted-foreground text-sm" data-testid="no-applications-message">
            {searchTerm || filterStatus !== 'all' || filterType !== 'all' || filterEvent !== 'all'
              ? 'No applications match your filters'
              : 'No staff applications yet'}
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredApplications.map((app) => (
            <Card
              key={app.id}
              data-testid={`application-card-${app.id}`}
              onClick={() => openDetail(app)}
              className="p-3 sm:p-4 hover:border-primary/50 transition-colors cursor-pointer bg-card border-border/50"
            >
              <div className="flex flex-col gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <h3 className="text-base sm:text-lg font-semibold truncate">{app.full_name}</h3>
                    <ScoreBadge score={app.score} />
                  </div>
                  <div className="flex flex-wrap gap-1 mb-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${statusColors[app.status]}`}>
                      {app.status.toUpperCase()}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${typeColors[app.application_type]}`}>
                      {app.application_type.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground truncate">{app.email}</p>
                  <p className="text-[10px] sm:text-xs text-primary mt-1 truncate">{app.event_name}</p>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {app.video_url && (
                      <Video className="h-4 w-4 text-primary" title="Has video" />
                    )}
                    <span className="text-[10px] sm:text-xs text-muted-foreground">
                      {formatDate(app.created_at)}
                    </span>
                  </div>
                  <Button variant="ghost" size="sm">
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="bg-card max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedApp && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-heading flex items-center gap-3">
                  {selectedApp.full_name}
                  <ScoreBadge score={selectedApp.score} />
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                {/* Status & Actions */}
                <div className="flex flex-wrap gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium border ${statusColors[selectedApp.status]}`}>
                    {selectedApp.status.toUpperCase()}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${typeColors[selectedApp.application_type]}`}>
                    {selectedApp.application_type.toUpperCase()}
                  </span>
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Email</p>
                    <p className="font-medium flex items-center gap-2">
                      <Mail className="h-4 w-4 text-primary" />
                      <a href={`mailto:${selectedApp.email}`} className="hover:text-primary">{selectedApp.email}</a>
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Phone</p>
                    <p className="font-medium">{selectedApp.phone}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Social Media</p>
                    <p className="font-medium">{selectedApp.social_media_handle}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Event</p>
                    <p className="font-medium flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-primary" />
                      {selectedApp.event_name}
                    </p>
                  </div>
                </div>

                {/* Experience */}
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Convention Experience</p>
                  <p className="font-medium capitalize">{selectedApp.worked_conventions_before}</p>
                  {selectedApp.previous_experience && (
                    <p className="text-sm text-muted-foreground mt-1">{selectedApp.previous_experience}</p>
                  )}
                </div>

                {/* Questions */}
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Favorite Anime</p>
                    <p className="font-medium">{selectedApp.favorite_anime}</p>
                    <p className="text-sm text-muted-foreground mt-1">{selectedApp.favorite_anime_reason}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Why Great Fit</p>
                    <p className="text-sm">{selectedApp.why_great_fit}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">POC Representation</p>
                    <p className="text-sm">{selectedApp.poc_representation_importance}</p>
                  </div>
                  {selectedApp.additional_details && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Additional Details</p>
                      <p className="text-sm">{selectedApp.additional_details}</p>
                    </div>
                  )}
                </div>

                {/* Video */}
                {selectedApp.video_url && (() => {
                  const { external, embed } = parseVideoUrl(selectedApp.video_url);
                  return (
                    <div data-testid="applicant-video-section">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Video Introduction</p>
                      {embed ? (
                        <div className="relative w-full overflow-hidden rounded-lg border border-border bg-black" style={{ aspectRatio: '16 / 9' }}>
                          <iframe
                            src={embed}
                            title="Applicant video introduction"
                            data-testid="applicant-video-iframe"
                            className="absolute inset-0 w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground mb-2">
                          This video can't be embedded. Use the button below to open it in a new tab.
                        </p>
                      )}
                      <a 
                        href={external} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        data-testid="applicant-video-link"
                        className="inline-flex items-center gap-2 px-4 py-2 mt-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 transition-colors"
                      >
                        <Video className="h-4 w-4" />
                        Open Video in New Tab
                      </a>
                    </div>
                  );
                })()}

                {/* Notes */}
                <div>
                  <Label htmlFor="notes" className="text-xs text-muted-foreground uppercase tracking-wider">
                    Internal Notes
                  </Label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add notes about this applicant..."
                    rows={3}
                    className="mt-2 bg-secondary/50 border-transparent focus:border-primary"
                  />
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
                  {selectedApp.status === 'pending' && (
                    <>
                      <Button
                        onClick={() => handleStatusUpdate(selectedApp.id, 'reviewed')}
                        className="bg-blue-500 hover:bg-blue-600"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Mark Reviewed
                      </Button>
                      <Button
                        onClick={() => handleStatusUpdate(selectedApp.id, 'accepted')}
                        className="bg-green-500 hover:bg-green-600"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Accept
                      </Button>
                      <Button
                        onClick={() => handleStatusUpdate(selectedApp.id, 'rejected')}
                        variant="destructive"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </>
                  )}
                  {selectedApp.status === 'reviewed' && (
                    <>
                      <Button
                        onClick={() => handleStatusUpdate(selectedApp.id, 'accepted')}
                        className="bg-green-500 hover:bg-green-600"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Accept
                      </Button>
                      <Button
                        onClick={() => handleStatusUpdate(selectedApp.id, 'rejected')}
                        variant="destructive"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </>
                  )}
                  <Button
                    onClick={() => handleDelete(selectedApp.id)}
                    variant="outline"
                    className="text-red-400 border-red-500/50 hover:bg-red-500/10"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
